// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';
import DrawingFeature, { DrawingShape } from '../../tools/drawing/drawingFeature';
import Map2dComponent from '../map-2d/component';
import OlDrawing from '../../tools/drawing/olDrawing';
import DrawingState from '../../tools/drawing/drawingState';
import { UsedInTemplateOnly } from '../../decorators';

class DrawingToolbarComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = ['../../styles/common.css', './style.css'];

  private readonly stateLocationAttribute = 'stateLocation';

  private readonly buttonConfig: { id: string; shape: DrawingShape | null; imgSrc: string; toolTip: string }[] = [
    { shape: null, imgSrc: 'icons/arrow.svg', toolTip: 'Disable drawing' },
    { shape: DrawingShape.Point, imgSrc: 'icons/point.svg', toolTip: 'Point' },
    { shape: DrawingShape.Polyline, imgSrc: 'icons/polyline.svg', toolTip: 'Polyline' },
    { shape: DrawingShape.Square, imgSrc: 'icons/square.svg', toolTip: 'Square' },
    { shape: DrawingShape.Rectangle, imgSrc: 'icons/rectangle.svg', toolTip: 'Rectangle' },
    { shape: DrawingShape.Polygon, imgSrc: 'icons/polygon.svg', toolTip: 'Polygon' },
    { shape: DrawingShape.Disk, imgSrc: 'icons/circle.svg', toolTip: 'Circle' },
    { shape: DrawingShape.FreehandPolyline, imgSrc: 'icons/polyline-free.svg', toolTip: 'Freehand line' },
    { shape: DrawingShape.FreehandPolygon, imgSrc: 'icons/polygon-free.svg', toolTip: 'Freehand polygon' }
  ].map((b) => ({ ...b, id: b.shape === null ? 'disable' : DrawingShape[b.shape] }));

  private drawingState!: DrawingState;
  private stateLocation!: string;
  private olDrawing!: OlDrawing;
  private drawingTool: Element | null = null;

  private get map() {
    return this.context.mapManager.getMap();
  }

  private get mapComponent() {
    return this.context.componentManager.getComponents(Map2dComponent)[0];
  }

  constructor() {
    super('advanced-filter-drawing-toolbar');
  }

  private getRequiredStateLocation(): string {
    const stateLocation = this.getAttribute(this.stateLocationAttribute);
    if (!stateLocation) {
      throw new Error(`Attribute "${this.stateLocationAttribute}" is required for DrawingToolbarComponent`);
    }
    return stateLocation;
  }

  private setTool(tool: DrawingShape | null = null) {
    if (this.drawingTool !== null) {
      this.drawingTool.classList.remove('selected');
    }
    if (tool === null) {
      // Unregister drawing interactions
      this.olDrawing.unregisterInteractions();
    } else {
      this.drawingTool = this.getButtonByDrawingShape(tool);
      this.drawingTool.classList.add('selected');
      // Activate drawing interactions
      this.olDrawing.registerInteractions();
      // Remove previous features
      this.drawingState.features = [];
    }
    this.drawingState.activeTool = tool;
  }

  onFeaturesChanged(oldFeatures: DrawingFeature[], newFeatures: DrawingFeature[]) {
    oldFeatures = oldFeatures ?? [];
    const newIds = new Set(newFeatures.map((f) => f.id));
    const oldIds = new Set(oldFeatures.map((f) => f.id));
    const deleted = oldFeatures.filter((f) => !newIds.has(f.id));
    const added = newFeatures.filter((f) => !oldIds.has(f.id));

    // Update drawing source
    if (deleted.length > 0) this.olDrawing.deleteFeatures(deleted);
    if (added.length > 0) this.olDrawing.addFeatures(added);

    if (this.drawingState.activeTool) {
      this.setTool(null);
    }
    this.refreshRender();
  }

  registerEvents() {
    this.subscribe(`extendedState.${this.stateLocation}.features`, (olds, news) => this.onFeaturesChanged(olds, news));
    this.subscribe(new RegExp(String.raw`extendedState.${this.stateLocation}.tools.*\.enabled`, 'g'), () =>
      this.refreshRender()
    );
  }

  private addDrawHandlers() {
    for (const tool of this.drawingState.tools) {
      this.getButtonByDrawingShape(tool.shape).addEventListener('pointerup', () => {
        if (this.drawingState.activeTool === tool.shape) {
          this.setTool(null);
        } else {
          this.setTool(tool.shape);
        }
        this.refreshRender();
      });
    }
  }

  private getButtonByDrawingShape(shape: DrawingShape | null): HTMLElement {
    const btnId = this.buttonConfig.find((b) => b.shape === shape)?.id ?? '';
    const btn = this.getById(btnId);
    if (!btn) {
      throw new Error(`Button for tool ${shape} not found in DrawingToolbarComponent`);
    }
    return btn;
  }

  @UsedInTemplateOnly()
  public isToolButtonEnabled(shape: DrawingShape | null): boolean {
    return this.drawingState?.tools.find((t) => t.shape === shape)?.enabled ?? false;
  }

  @UsedInTemplateOnly()
  public isButtonVisible(shape: DrawingShape | null): boolean {
    return !!this.drawingState?.tools.find((t) => t.shape === shape);
  }

  protected override connectedCallback() {
    super.connectedCallback();

    // Readout state name from HTML attribute
    this.stateLocation = this.getRequiredStateLocation();

    // Setup drawing state, ol drawing tools and drawing layer
    if (!this.state.extendedState[this.stateLocation]) {
      this.context.stateManager.state.extendedState[this.stateLocation] = new DrawingState();
    }
    this.drawingState = this.state.extendedState[this.stateLocation] as DrawingState;
    this.olDrawing = new OlDrawing(this.mapComponent, this.stateLocation, this.context, () => {});
    this.map.addLayer(this.olDrawing.drawingLayer);

    super.render();
    this.girafeTranslate();

    this.registerEvents();
    this.addDrawHandlers();

    this.setTool(this.drawingState.activeTool);
  }
}

export default DrawingToolbarComponent;
